'use client';
import {cn} from '@/app/lib/utils';
import clsx from 'clsx';
import {Locale, useTranslations} from 'next-intl';
import Link from 'next/link';
import React, {useEffect, useState} from 'react';
import {target} from './helpers/utils';

interface MenuItem {
  url: string;
  name: string;
  items?: MenuItem[];
}

interface NavigationMenuProps {
  lang: Locale;
  menu: MenuItem[];
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({lang, menu}) => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const t = useTranslations('Navigation');

  useEffect(() => {
    setMenus(menu);
  }, []);

  const toggleMenu = (name: string) => {
    if (window.innerWidth <= 768) {
      setOpenMenu(openMenu === name ? null : name);
      setOpenSubMenu(null); // Reset submenu when main menu toggles
    }
  };

  const toggleSubMenu = (name: string) => {
    setOpenSubMenu(openSubMenu === name ? null : name);
  };

  const [view, setView] = useState('desktop');
  const handleView = () => {
    window.innerWidth >= 1200 ? setView('desktop') : setView('mobile');
  };

  useEffect(() => {
    window.addEventListener('resize', handleView);
    handleView();
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const itemRefs = React.useRef<{[key: string]: HTMLDivElement | null}>({});

  return (
    <div className="bg-[#e22319] header-font relative menu-bar">
      {view == 'mobile' && (
        <>
          <div className=" flex justify-end items-center h-[35px]">
            <button
              onClick={() => {
                setOpenMobileMenu(!openMobileMenu);
              }}
              className="h-[50px] mr-2 flex items-center justify-center cursor-pointer"
            >
              <div className="material-symbols outlined text-white ">menu</div>
            </button>
          </div>
          <nav
            className={cn('flex-col justify-between gap-5', {
              'hidden ': !openMobileMenu
            })}
            aria-label="Navigation Menu Mobile"
          >
            {false && (
              <div className="p-2 md:hidden">
                <div className="w-full rounded-full bg-white flex items-center justify-between">
                  <div className="material-symbols outlined text-[#ddd] bg-transparent pl-[5px]">
                    search
                  </div>
                  <input
                    type="text"
                    className="text-[#333] w-full bg-transparent pl-2 font-[100]"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key == 'Enter') {
                        if (lang == 'zh') {
                          window.location.href = `https://www.aswatson.com/zh/search/?s=${searchQuery}&submit=Search`;
                        } else {
                          window.location.href = `https://www.aswatson.com/search/?s=${searchQuery}&submit=Search`;
                        }
                      }
                    }}
                    style={{
                      fontFamily: '"Helvetica Neue",Helvetica,Arial,sans-serif'
                    }}
                    placeholder={t('search')}
                  />
                </div>
              </div>
            )}
            {menus.map((item: MenuItem) => {
              return (
                <div
                  key={item.name}
                  className={cn('relative group')}
                  onMouseEnter={() => {
                    if (window.innerWidth > 768) {
                      setOpenMenu(item.name);
                    }
                  }}
                  onMouseLeave={() => {
                    if (window.innerWidth > 768) {
                      setOpenMenu(null);
                    }
                  }}
                  ref={(el) => {
                    itemRefs.current[item.name] = el;
                  }}
                  onClick={(e) => {
                    if (!item.items) {
                      window.location.href = item.url;
                    }
                    if (window.innerWidth > 768) {
                      if (openMenu === item.name) {
                        setOpenMenu(null);
                      } else {
                        setOpenMenu(item.name);
                      }
                    }
                  }}
                >
                  <button
                    className={cn(
                      'flex items-center justify-between w-full text-white text-[20px] font-[700] px-2 py-1 md:px-0'
                    )}
                    onClick={() => {
                      toggleMenu(item.name);

                      const element = itemRefs.current[item.name];
                      if (element) {
                        setTimeout(() => {
                          const offset = 80; // 這裡可以設定你的 Fixed Header 高度，避免被遮擋
                          const top =
                            element.getBoundingClientRect().top +
                            window.scrollY -
                            offset;
                          window.scrollTo({
                            top,
                            behavior: 'smooth'
                          });
                        }, 150); // 延遲 150ms 確保下拉選單 DOM 已經完全展開，高度計算才會準確
                      }
                    }}
                  >
                    <Link
                      prefetch={false}
                      href={item.url}
                      locale={lang}
                      target={target(item.url)}
                      onClick={() => {
                        if (item.url) {
                          setOpenMobileMenu(false);
                        }
                      }}
                    >
                      {item.name}
                    </Link>
                    {item.items && item.items.length > 0 && (
                      <div
                        className={clsx(
                          'transition-all ',
                          openMenu === item.name ? 'rotate-180' : 'rotate-0'
                        )}
                      >
                        <span className="material-symbols outlined text-sm">
                          keyboard_arrow_down
                        </span>
                      </div>
                    )}
                  </button>
                  {item.items && item.items.length > 0 && (
                    <div
                      className={`${
                        openMenu === item.name ? '' : 'hidden'
                      } left-0 w-full mb-2 z-10  text-white px-2 `}
                    >
                      {item.items.map((subItem) => (
                        <div
                          key={subItem.name}
                          className="relative group text-[16px]"
                        >
                          <div className="flex items-center">
                            <a
                              href={subItem.url}
                              className={cn(
                                'block mx-5  p-2 border-b border-[#eeeeeeb3] w-full text-left leading-[1.75] ',
                                lang == 'en' && 'questrial'
                              )}
                              target={target(subItem.url)}
                            >
                              {subItem.name}
                            </a>
                            {subItem.items && (
                              <button
                                className="hidden p-2"
                                onClick={() => toggleSubMenu(subItem.name)}
                              ></button>
                            )}
                          </div>
                          {subItem.items && (
                            <div className={`px-5 `}>
                              {subItem.items.map((nestedItem) => (
                                <a
                                  key={nestedItem.name}
                                  href={nestedItem.url}
                                  className={cn(
                                    'block mx-5 p-2 border-b border-[#eeeeeeb3] leading-[1.75]',
                                    lang == 'en' && 'questrial'
                                  )}
                                  target={target(nestedItem.url)}
                                >
                                  {nestedItem.name}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </>
      )}
      {view == 'desktop' && (
        <nav
          className={cn(
            'w-full mx-auto flex-col md:flex md:flex-row justify-between max-w-[1080px] ',
            {
              'hidden ': !openMobileMenu
            }
          )}
          aria-label="Navigation Menu Desktop"
        >
          {menus.map((item: MenuItem) => (
            <div
              key={item.name}
              className={cn('relative group')}
              onMouseEnter={() => {
                if (window.innerWidth > 768) {
                  setOpenMenu(item.name);
                }
              }}
              onMouseLeave={() => {
                if (window.innerWidth > 768) {
                  setOpenMenu(null);
                }
              }}
              onClick={(e) => {
                if (!item.items) {
                  window.location.href = item.url;
                }
                if (window.innerWidth > 768) {
                  if (openMenu === item.name) {
                    setOpenMenu(null);
                  } else {
                    setOpenMenu(item.name);
                  }
                }
              }}
            >
              <button
                className={cn(
                  ' p-1 flex items-center justify-between w-full md:w-auto text-white md:text-[15px] text-[22px] font-[700]',
                  openMenu === item.name && 'md:bg-white md:text-[#e22319]'
                )}
                onClick={() => {
                  toggleMenu(item.name);
                }}
              >
                <Link
                  prefetch={false}
                  href={item.url}
                  locale={lang}
                  target={target(item.url)}
                  onClick={() => {
                    if (item.url) {
                      setOpenMobileMenu(false);
                    }
                  }}
                >
                  {item.name}
                </Link>
                {item.items && item.items.length > 0 && (
                  <span className="material-symbols outlined md:hidden text-sm">
                    keyboard_arrow_down
                  </span>
                )}
              </button>
              {item.items && item.items.length > 0 && (
                <div
                  className={`${
                    openMenu === item.name ? 'block' : 'hidden'
                  } md:group-hover:block left-0 md:bg-white w-full mb-2 md:mb-0 md:w-64 z-10 md:shadow-lg text-white md:text-[#58585a] px-2 md:py-3 md:absolute md:rounded-b-lg`}
                >
                  {item.items.map((subItem) => (
                    <div
                      key={subItem.name}
                      className="relative group md:text-[14px] text-[16px]"
                    >
                      <div className="flex items-center md:justify-between">
                        <a
                          href={subItem.url}
                          className={cn(
                            'block mx-5 md:mx-0 p-2 border-b border-[#eeeeeeb3] md:border-0 md:hover:text-[#e22319] w-full text-left leading-[1.75] ',
                            lang == 'en' && 'questrial'
                          )}
                          target={target(subItem.url)}
                        >
                          {subItem.name}
                        </a>
                        {subItem.items && subItem.items.length > 0 && (
                          <button
                            className=" p-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSubMenu(subItem.name);
                            }}
                            onMouseEnter={() => setOpenSubMenu(subItem.name)}
                            aria-label={
                              openSubMenu == subItem.name
                                ? t('less')
                                : t('more')
                            }
                          >
                            <div
                              className={cn(
                                'flex items-center justify-center  transition-all h-5 w-5',
                                openSubMenu === subItem.name
                                  ? '-rotate-90'
                                  : 'rotate-90'
                              )}
                            >
                              <span
                                className={cn(
                                  'material-symbols outlined  text-sm'
                                )}
                              >
                                chevron_right
                              </span>
                            </div>
                          </button>
                        )}
                      </div>
                      {subItem.items && (
                        <div
                          className={clsx(
                            `px-5 md:pr-0 md:pl-4`,
                            openSubMenu === subItem.name ? 'block' : 'md:hidden'
                          )}
                        >
                          {subItem.items.map((nestedItem) => (
                            <a
                              key={nestedItem.name}
                              href={nestedItem.url}
                              className={cn(
                                'block mx-5 md:mx-0 p-2 border-b border-[#eeeeeeb3] md:border-0 md:hover:text-[#e22319] leading-[1.75]',
                                lang == 'en' && 'questrial'
                              )}
                              target={target(nestedItem.url)}
                            >
                              {nestedItem.name}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      )}
    </div>
  );
};

export default NavigationMenu;
